/** @file applyScope: a template puppet narrowed to a creation scope (directions, pitch, camera; CR-002). */
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import type { CommandContext } from '../context/commandContext';
import { requireSectorTable } from '../facet/requireSectorTable';
import { retargetEntry } from '../facet/retargetEntry';
import type { TemplateScope } from '../types/puppetInputs';
import { requireProfile } from '../validate/requireProfile';
import { followAngles } from './followAngles';
import { scopeBandsOf } from './scopeBandsOf';
import { scopePartialWarning } from './scopePartialWarning';
import { scopeTableOf } from './scopeTableOf';

type Directions = NonNullable<TemplateScope['directions']>;
type Tables = Pick<CommandContext, 'sectorTables'>;

/** Moves `entry` to its family member for `directions`; false when it has no member and stays wider. */
function narrowEntry(p: Puppet, entry: FacetEntry, directions: Directions, ctx: Tables, path: string): boolean {
  const from = requireSectorTable(p, entry.yaw, ctx.sectorTables, `${path}/yaw`);
  const id = scopeTableOf(entry.yaw, directions);
  if (id === undefined) return from.thresholds.length <= directions;
  if (id !== entry.yaw) {
    const to = requireSectorTable(p, id, ctx.sectorTables, `${path}/yaw`);
    retargetEntry(entry, to, followAngles(entry, from, to), path);
  }
  return true;
}

/**
 * A copy of `puppet` narrowed to `scope`: `pitch` keeps each impostor's and effect's entries per `scopeBandsOf`;
 * `directions` moves every entry to its table family member through the `facet.setTable` rule (drawn angles follow,
 * slots of vanished tiles go) and caps `facing.steps` at the count (`free` becomes the count); an entry without a
 * member stays and yields a `SCOPE_PARTIAL`; `camera: 'side'` swaps the profile for its `<profile>-side` twin
 * (PROFILE_UNKNOWN at `/input/scope/camera` when the context lacks it). Absent fields change nothing.
 */
export function applyScope(puppet: Puppet, scope: TemplateScope, ctx: Pick<CommandContext, 'sectorTables' | 'profiles'>): { puppet: Puppet; warnings: Warning[] } {
  const p = structuredClone(puppet);
  const { directions, pitch, camera } = scope;
  const warnings: Warning[] = [];
  p.drawables.forEach((d, di) => {
    if ((d.kind !== 'impostor' && d.kind !== 'effect') || d.facets === undefined) return;
    if (pitch !== undefined) d.facets = scopeBandsOf(d.facets, pitch);
    if (directions === undefined) return;
    d.facets.forEach((entry, i) => {
      if (!narrowEntry(p, entry, directions, ctx, `/puppet/drawables/${String(di)}/facets/${String(i)}`)) {
        warnings.push(scopePartialWarning(d.id, di, i, entry.yaw, directions));
      }
    });
  });
  if (directions !== undefined) {
    const steps = p.facing.steps;
    p.facing = { ...p.facing, steps: steps === 'free' ? directions : Math.min(steps, directions) };
  }
  if (camera === 'side' && !p.profile.endsWith('-side')) p.profile = requireProfile(`${p.profile}-side`, ctx, '/input/scope/camera');
  return { puppet: p, warnings };
}
